import { z } from 'zod';
import { PRICED_CLEARANCE_TIERS, PRICED_VERSION_PACKS, SCRIPT_LENGTHS } from './products/script';
import { SCOPES, TURNAROUND_DAYS } from './products/shared';

/** A price: integer minor units (cents), positive, within a Postgres `integer`. Never a float. */
export const minorUnitsSchema = z.number().int().positive().max(2_147_483_647);

/** A strict object with one price per key. Strict, so a typo in a key is refused, not priced from another cell. */
function cells<K extends string | number>(keys: readonly K[]) {
  return rows(
    keys.map((k) => `${k}` as const),
    minorUnitsSchema,
  );
}
function rows<K extends string, V extends z.ZodType>(keys: readonly K[], value: V) {
  return z.strictObject(Object.fromEntries(keys.map((k) => [k, value])) as Record<K, V>);
}

const byTurnaround = cells(TURNAROUND_DAYS);
const titleMatrixSchema = rows(SCOPES, byTurnaround);
const scriptMatrixSchema = z.strictObject({
  base: rows(SCRIPT_LENGTHS, byTurnaround),
  // the included tier and "no versions" cost nothing and have no cell
  additional_clearances: cells(PRICED_CLEARANCE_TIERS),
  versions: cells(PRICED_VERSION_PACKS),
});

/** The matrix a price list version is published with: every product, every cell, in the list's single currency. */
export const priceMatrixSchema = z.strictObject({
  title: titleMatrixSchema,
  script: scriptMatrixSchema,
});
export type PriceMatrix = z.infer<typeof priceMatrixSchema>;
